import {
  billableDestinationLabel,
  billableSourceLabel,
  billingSideLabel,
  selectableBillableItemIds,
} from "./billingPresentation.js";

const MISSING_ACCOUNT_KEY = "__none__";

function clean(value) {
  return String(value ?? "").trim();
}

function numberValue(value) {
  return Number.isFinite(Number(value)) ? Number(value) : 0;
}

function compareText(left, right) {
  return clean(left).localeCompare(clean(right), "fr-CA", { sensitivity: "base" });
}

function compareDeliveryDateDesc(left, right) {
  const leftTime = Date.parse(left?.delivery_date || left?.service_date || "");
  const rightTime = Date.parse(right?.delivery_date || right?.service_date || "");
  if (Number.isFinite(leftTime) && Number.isFinite(rightTime) && leftTime !== rightTime) {
    return rightTime - leftTime;
  }
  return compareText(
    left?.order_reference || left?.order_number || left?.id,
    right?.order_reference || right?.order_number || right?.id,
  );
}

function accountKey(item) {
  return clean(item?.billing_account_id || item?.billing_account?.id) || MISSING_ACCOUNT_KEY;
}

function clientKey(item) {
  return clean(item?.source_client_correspondence_id || item?.source_client?.id)
    || `__client_name__:${billableSourceLabel(item)}`;
}

function sideGroupKey(item) {
  const side = clean(item?.billing_side) || "__side_none__";
  const currency = clean(item?.currency_code) || "CAD";
  return `${side}::${currency}`;
}

function buildAggregates(items) {
  const eligibleIds = selectableBillableItemIds(items);
  const totalsByCurrency = new Map();
  const unknownCurrencies = new Set();

  for (const item of items) {
    const currency = clean(item?.currency_code) || "CAD";
    if (item?.pricing_pending) unknownCurrencies.add(currency);
    const amount = numberValue(item?.line_total ?? item?.total_amount);
    totalsByCurrency.set(currency, numberValue(totalsByCurrency.get(currency)) + amount);
  }

  const blockedCount = items.length - eligibleIds.length;

  return {
    itemCount: items.length,
    eligibleCount: eligibleIds.length,
    blockedCount,
    eligibleIds,
    totalsByCurrency: [...totalsByCurrency.entries()].map(([currencyCode, amount]) => ({
      currencyCode,
      amount: unknownCurrencies.has(currencyCode) ? null : amount,
    })),
  };
}

function buildSideGroup(accountBucketKey, clientBucketKey, sideBucket) {
  const sortedItems = [...sideBucket.items].sort(compareDeliveryDateDesc);
  const billingSide = clean(sideBucket.key).split("::")[0] || "";
  return {
    level: "billing_side",
    key: `${accountBucketKey}::${clientBucketKey}::${sideBucket.key}`,
    label: sideBucket.label,
    billingSide,
    items: sortedItems,
    ...buildAggregates(sortedItems),
  };
}

function buildClientGroup(accountBucketKey, clientBucket) {
  const sideGroups = [...clientBucket.sideGroups.values()]
    .sort((left, right) => compareText(left.label, right.label))
    .map((sideBucket) => buildSideGroup(accountBucketKey, clientBucket.key, sideBucket));
  const clientItems = sideGroups.flatMap((sideGroup) => sideGroup.items);

  return {
    level: "client",
    key: `${accountBucketKey}::${clientBucket.key}`,
    label: clientBucket.label,
    children: sideGroups,
    items: clientItems,
    ...buildAggregates(clientItems),
  };
}

export function groupBillableItems(items) {
  const sourceItems = Array.isArray(items) ? items : [];
  const accountBuckets = new Map();

  for (const item of sourceItems) {
    const accountBucketKey = accountKey(item);
    if (!accountBuckets.has(accountBucketKey)) {
      accountBuckets.set(accountBucketKey, {
        key: accountBucketKey,
        label: billableDestinationLabel(item),
        clients: new Map(),
      });
    }

    const accountBucket = accountBuckets.get(accountBucketKey);
    const clientBucketKey = clientKey(item);
    if (!accountBucket.clients.has(clientBucketKey)) {
      accountBucket.clients.set(clientBucketKey, {
        key: clientBucketKey,
        label: billableSourceLabel(item),
        sideGroups: new Map(),
      });
    }

    const clientBucket = accountBucket.clients.get(clientBucketKey);
    const sideBucketKey = sideGroupKey(item);
    if (!clientBucket.sideGroups.has(sideBucketKey)) {
      clientBucket.sideGroups.set(sideBucketKey, {
        key: sideBucketKey,
        label: billingSideLabel(item?.billing_side),
        items: [],
      });
    }

    clientBucket.sideGroups.get(sideBucketKey).items.push(item);
  }

  return [...accountBuckets.values()]
    .sort((left, right) => compareText(left.label, right.label))
    .map((accountBucket) => {
      const clients = [...accountBucket.clients.values()]
        .sort((left, right) => compareText(left.label, right.label))
        .map((clientBucket) => buildClientGroup(accountBucket.key, clientBucket));
      const accountItems = clients.flatMap((client) => client.items);

      return {
        level: "account",
        key: accountBucket.key,
        label: accountBucket.label,
        children: clients,
        items: accountItems,
        ...buildAggregates(accountItems),
      };
    });
}


function attachCardSides(account) {
  const sides = [];
  const sideIndex = new Map();
  for (const client of account.children || []) {
    for (const side of client.children || []) {
      const key = clean(side.billingSide);
      if (!sideIndex.has(key)) {
        sideIndex.set(key, sides.length);
        sides.push({
          billingSide: side.billingSide,
          label: side.label,
          itemCount: 0,
          eligibleCount: 0,
          blockedCount: 0,
        });
      }
      const bucket = sides[sideIndex.get(key)];
      bucket.itemCount += numberValue(side.itemCount);
      bucket.eligibleCount += numberValue(side.eligibleCount);
      bucket.blockedCount += numberValue(side.blockedCount);
    }
  }
  return { ...account, sides };
}

export function groupBillableAccountsForCards(items) {
  return groupBillableItems(items)
    .flatMap((account) => {
      if (account.key !== MISSING_ACCOUNT_KEY) return [account];
      return (account.children || []).map((client) => ({
        ...client,
        level: "account",
        children: [client],
      }));
    })
    .map(attachCardSides)
    .sort((left, right) => compareText(left.label, right.label));
}
